import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { EventType } from 'constants/enums'
import { MAX_VEHICLES } from '../car-comparison/constants'
import { CarComparisonStore } from '../store'
import { AbstractCard } from '../abstract-card'
import { TrimSelectorBase } from '../trim-selector/trim-selector-base'
import { getResultsUrlPath, isMyPicksPage, isVehicleInMyPicks } from '../util'
import { ApiService } from 'services/api/api.service'
import { logger } from 'utils/system/logger'
import { IDriving, IDrivingVehicle } from '../driving-interface'

const { comparisonResults: resultClass } = CLASS_NAME
const {
    driving: { vehicleCard: attrName }
} = ATTR_NAME

/**
 * Model Card Component 🚘
 */
export abstract class VehicleCard extends AbstractCard {
    protected url: URL = new URL(window.location.href)

    protected get openTrimSelectorButtonClass(): string {
        return `.${resultClass.buttonEditMyPicks}`
    }

    protected get makeNameClass(): string {
        return `.${resultClass.makeName}`
    }

    protected get modelNameClass(): string {
        return `.${resultClass.modelName}`
    }

    protected get yearAttr(): string {
        return attrName.year
    }

    protected get makeAttr(): string {
        return attrName.make
    }

    protected get modelAttr(): string {
        return attrName.model
    }

    // get trim trimselector from child cards
    // Ex. MyPicksPageCard, ComparisonResultsCard
    abstract get trimSelector(): TrimSelectorBase

    public constructor(public wrapper: HTMLElement) {
        super(wrapper)
    }

    /**
     * Bind HTML Element for comparison result page
     * @returns {boolean} Indicate if the HTML structure is on the screen
     */
    protected bindHTMLElements(): boolean {
        if (!super.bindHTMLElements()) {
            return false
        }

        const addToMyPicksButton = this.getElement<HTMLButtonElement>(
            `.${resultClass.buttonAddToMyPicks}`,
            this.wrapper
        )
        const buttonEditMyPicks = this.getElement<HTMLButtonElement>(
            `.${resultClass.buttonEditMyPicks}`,
            this.wrapper
        )
        const removeResultCardButton = this.getElement<HTMLButtonElement>(
            `.${resultClass.buttonRemoveCard}`,
            this.wrapper
        )
        if (
            !addToMyPicksButton ||
            !buttonEditMyPicks ||
            !removeResultCardButton
        ) {
            return false
        }
        this.addToMyPicksButton = addToMyPicksButton
        this.buttonEditMyPicks = buttonEditMyPicks
        this.removeResultCardButton = removeResultCardButton

        return true
    }

    /**
     * Bind data attributes for find compare page
     * @returns {boolean} indicate if the information exists
     */
    protected bindDataAttributes(): boolean {
        super.bindDataAttributes()

        const vehicleCardID = this.wrapper.getAttribute(attrName.vehicleCardID)
        if (!vehicleCardID) {
            return false
        }
        this.vehicleCardID = vehicleCardID

        return true
    }

    /**
     * Bind data attributes for comparison result page
     * @returns {boolean} indicate if the information exists
     */
    protected bindListeners(): void {
        super.bindListeners()

        // Add trim in my picks if there is no trim
        this.addToMyPicksButton.addEventListener(EventType.CLICK, async () => {
            await this.addToMyPicks()
        })

        this.removeResultCardButton.addEventListener(EventType.CLICK, () => {
            this.removeResultCard()
        })
    }

    private async addToMyPicks(): Promise<void> {
        await CarComparisonStore.getInstance()
            .add(parseInt(this.vehicleCardID))
            .then(() => {
                if (isMyPicksPage()) {
                    // reload if the result page matches same my picks session
                    void window.location.assign(this.url.toString())
                } else {
                    // focus on modify trim button
                    this.buttonEditMyPicks.focus()
                }
            })
    }

    /**
     * Remove vehicle card in comparison result page
     */
    private removeResultCard(): void {
        const updatedItems = this.url.pathname
            .split('/')[2]
            .split('-')
            .filter((id) => id != this.vehicleCardID)

        // remove vehicle in my picks session
        if (
            isVehicleInMyPicks(
                CarComparisonStore.getInstance().get(),
                this.vehicleCardID
            )
        ) {
            CarComparisonStore.getInstance().remove(
                parseInt(this.vehicleCardID)
            )
        }

        if (updatedItems.length) {
            // reload page after remove vehicle in my picks session
            new ApiService()
                .fetchDrivingVehicles<IDriving<IDrivingVehicle>>({
                    id: updatedItems.join(',')
                })
                .then(async (data: IDriving<IDrivingVehicle>) => {
                    this.url.pathname = getResultsUrlPath(data['results'])
                    window.location.assign(this.url.toString())
                })
                .catch((err) => {
                    logger('error', `Vehicle API: ${err}`)
                })
        } else {
            // stay with blank result page
            // remove=1 will keep to stay even though there is my picks session
            this.url.pathname =
                this.url.pathname.split('/')[0] +
                this.url.pathname.split('/')[1]
            window.location.assign(`${this.url}/?remove=1`)
        }
    }

    /**
     * Set the state comparing with store (already added/ compare)
     */
    public setCardState(): void {
        const vehicles = CarComparisonStore.getInstance().get()

        this.openTrimSelectorButton.hidden = true
        this.addToMyPicksButton.hidden = true

        // Set the button to edit/add for my picks
        if (isVehicleInMyPicks(vehicles, this.vehicleCardID)) {
            this.openTrimSelectorButton.hidden = false
            this.openTrimSelectorButton.disabled = false
        } else {
            this.addToMyPicksButton.hidden = false
            // disable add to my picks button
            if (vehicles.length >= MAX_VEHICLES) {
                this.addToMyPicksButton.disabled = true
            }
        }
    }
}
